import { get, groupBy, isEmpty, isNil } from "lodash";
import { EDGE_KINDS, NODE_TYPES } from "utils/constants";
import { isPublishBlocked } from "../../ScreenStudios/Detail/BindingReview";
import { checkedFieldIssues } from "./checked-fields";
import { isRoutingEdge } from "./graph";
import { needsCategoryOf } from "./needs-fields";
import {
  borrowsPublishedScreen,
  delayBlocker,
  describeRules,
  EXIT_TYPES,
  exitsOf,
  exitType,
  isInputStep,
  RULE_MODES,
} from "./next-steps";
import {
  needsPathsOfStep,
  showsRecommendedBadge,
  slotPathOf,
  slotsOfStep,
} from "./recommendation-guide";
import { isWaitingType, STEP_TABS } from "./step-tabs";

export const CHECK_LEVELS = {
  ERROR: "ERROR",
  WARNING: "WARNING",
};

export const CHECK_CODES = {
  SCREEN_NOT_SHOWN: "SCREEN_NOT_SHOWN",
  SCREEN_FORM_INSTEAD: "SCREEN_FORM_INSTEAD",
  SCREEN_LINKS_BLOCKED: "SCREEN_LINKS_BLOCKED",
  SCREEN_LISTS_MANUAL: "SCREEN_LISTS_MANUAL",
  RULE_NEVER_USED: "RULE_NEVER_USED",
  RULE_INCOMPLETE: "RULE_INCOMPLETE",
  DELAY_NEVER_FIRES: "DELAY_NEVER_FIRES",
  EVENT_UNNAMED: "EVENT_UNNAMED",
  TIMER_NO_DURATION: "TIMER_NO_DURATION",
  NO_FALLBACK: "NO_FALLBACK",
  UNREACHABLE: "UNREACHABLE",
  AUTO_LOOP: "AUTO_LOOP",
  RECOMMENDATION_WITHOUT_NEEDS: "RECOMMENDATION_WITHOUT_NEEDS",
  NEEDS_CATEGORY_MISSING: "NEEDS_CATEGORY_MISSING",
  CHECKED_FIELD_NOT_LINKED: "CHECKED_FIELD_NOT_LINKED",
  CHECKED_FIELD_NO_CHECK: "CHECKED_FIELD_NO_CHECK",
  REQUIRED_NOT_ON_SCREEN: "REQUIRED_NOT_ON_SCREEN",
};

const LEVEL_BY_CODE = {
  [CHECK_CODES.SCREEN_NOT_SHOWN]: CHECK_LEVELS.ERROR,
  [CHECK_CODES.SCREEN_FORM_INSTEAD]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.SCREEN_LINKS_BLOCKED]: CHECK_LEVELS.ERROR,
  [CHECK_CODES.SCREEN_LISTS_MANUAL]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.RULE_NEVER_USED]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.RULE_INCOMPLETE]: CHECK_LEVELS.ERROR,
  [CHECK_CODES.DELAY_NEVER_FIRES]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.EVENT_UNNAMED]: CHECK_LEVELS.ERROR,
  [CHECK_CODES.TIMER_NO_DURATION]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.NO_FALLBACK]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.UNREACHABLE]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.AUTO_LOOP]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.RECOMMENDATION_WITHOUT_NEEDS]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.NEEDS_CATEGORY_MISSING]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.CHECKED_FIELD_NOT_LINKED]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.CHECKED_FIELD_NO_CHECK]: CHECK_LEVELS.WARNING,
  [CHECK_CODES.REQUIRED_NOT_ON_SCREEN]: CHECK_LEVELS.ERROR,
};

const TAB_BY_CODE = {
  [CHECK_CODES.SCREEN_LINKS_BLOCKED]: STEP_TABS.FIELDS,
  [CHECK_CODES.SCREEN_LISTS_MANUAL]: STEP_TABS.FIELDS,
  [CHECK_CODES.RULE_NEVER_USED]: STEP_TABS.NEXT,
  [CHECK_CODES.RULE_INCOMPLETE]: STEP_TABS.NEXT,
  [CHECK_CODES.DELAY_NEVER_FIRES]: STEP_TABS.NEXT,
  [CHECK_CODES.EVENT_UNNAMED]: STEP_TABS.NEXT,
  [CHECK_CODES.NO_FALLBACK]: STEP_TABS.NEXT,
  [CHECK_CODES.AUTO_LOOP]: STEP_TABS.NEXT,
  [CHECK_CODES.NEEDS_CATEGORY_MISSING]: STEP_TABS.FIELDS,
  [CHECK_CODES.CHECKED_FIELD_NOT_LINKED]: STEP_TABS.FIELDS,
  [CHECK_CODES.CHECKED_FIELD_NO_CHECK]: STEP_TABS.FIELDS,
  [CHECK_CODES.REQUIRED_NOT_ON_SCREEN]: STEP_TABS.FIELDS,
};

export const checkTab = (code) => TAB_BY_CODE[code] || STEP_TABS.MAIN;

const check = (code, params = {}, level = LEVEL_BY_CODE[code]) => ({
  code,
  level,
  params,
});

const screenChecks = (node, nodes) => {
  const data = node.data || {};
  if ((data.nodeType || NODE_TYPES.STATUS) !== NODE_TYPES.STATUS) return [];
  const hasScreen =
    data.screenCount > 0 || !isNil(get(data, "config.screenFrom"));
  const shown = data.screenPublished || borrowsPublishedScreen(node, nodes);
  const report = data.screenReport;
  const manualLists = (get(report, "manual") || []).length;
  return [
    ...(hasScreen && !isInputStep(node, nodes)
      ? [check(CHECK_CODES.SCREEN_NOT_SHOWN)]
      : []),
    ...(hasScreen && !shown && isInputStep(node, nodes)
      ? [check(CHECK_CODES.SCREEN_FORM_INSTEAD)]
      : []),
    ...(isPublishBlocked(report)
      ? [check(CHECK_CODES.SCREEN_LINKS_BLOCKED)]
      : []),
    ...(manualLists
      ? [check(CHECK_CODES.SCREEN_LISTS_MANUAL, { count: manualLists })]
      : []),
  ];
};

const ruleChecks = (node, edges) =>
  describeRules(edges, node.id).flatMap((rule) => [
    ...(rule.shadowedBy
      ? [check(CHECK_CODES.RULE_NEVER_USED, { rank: rule.rank })]
      : []),
    ...(rule.incomplete
      ? [check(CHECK_CODES.RULE_INCOMPLETE, { rank: rule.rank })]
      : []),
  ]);

const exitChecks = (node, nodes, edges) => {
  const exits = exitsOf(edges, node.id);
  const hasTimer = exits.some((edge) => exitType(edge) === EXIT_TYPES.TIMER);
  const unnamedEvents = exits.filter(
    (edge) =>
      exitType(edge) === EXIT_TYPES.EVENT && isEmpty(get(edge, "data.event")),
  );
  return [
    ...(hasTimer && delayBlocker(node, nodes, edges)
      ? [check(CHECK_CODES.DELAY_NEVER_FIRES)]
      : []),
    ...unnamedEvents.map(() => check(CHECK_CODES.EVENT_UNNAMED)),
  ];
};

const timerNodeChecks = (node) =>
  get(node, "data.nodeType") === NODE_TYPES.TIMER &&
  !(Number(get(node, "data.config.delaySeconds")) > 0)
    ? [check(CHECK_CODES.TIMER_NO_DURATION)]
    : [];

const fallbackChecks = (node, nodes, edges) => {
  const rules = describeRules(edges, node.id);
  if (isEmpty(rules)) return [];
  if (rules.some((rule) => rule.mode !== RULE_MODES.CONDITIONAL)) return [];
  const exits = exitsOf(edges, node.id);
  const namedEvent = exits.some(
    (edge) =>
      exitType(edge) === EXIT_TYPES.EVENT && !isEmpty(get(edge, "data.event")),
  );
  const firingTimer =
    exits.some((edge) => exitType(edge) === EXIT_TYPES.TIMER) &&
    !delayBlocker(node, nodes, edges);
  return namedEvent || firingTimer ? [] : [check(CHECK_CODES.NO_FALLBACK)];
};

export const stepChecks = (node, nodes = [], edges = []) => [
  ...screenChecks(node, nodes),
  ...ruleChecks(node, edges),
  ...fallbackChecks(node, nodes, edges),
  ...exitChecks(node, nodes, edges),
  ...timerNodeChecks(node),
];

const reachableFrom = (startId, edges) => {
  const edgesBySource = groupBy(edges, "source");
  const reached = new Set();
  const queue = [startId];
  while (queue.length) {
    const id = queue.shift();
    if (!reached.has(id)) {
      reached.add(id);
      for (const edge of edgesBySource[id] || []) queue.push(edge.target);
    }
  }
  return reached;
};

const isAutomatic = (node, nodes) => {
  const nodeType = get(node, "data.nodeType");
  return (
    !isInputStep(node, nodes) &&
    !isWaitingType(nodeType) &&
    nodeType !== NODE_TYPES.FORK
  );
};

const isUnconditional = (edge) =>
  get(edge, "data.kind") === EDGE_KINDS.ALWAYS ||
  isEmpty(get(edge, "data.conditions"));

const stronglyConnected = (ids, edgesBySource) => {
  const indexes = {};
  const lows = {};
  const stack = [];
  const onStack = new Set();
  const components = [];
  let counter = 0;
  const visit = (id) => {
    indexes[id] = counter;
    lows[id] = counter;
    counter += 1;
    stack.push(id);
    onStack.add(id);
    for (const edge of edgesBySource[id] || []) {
      if (isNil(indexes[edge.target])) {
        visit(edge.target);
        lows[id] = Math.min(lows[id], lows[edge.target]);
      } else if (onStack.has(edge.target)) {
        lows[id] = Math.min(lows[id], indexes[edge.target]);
      }
    }
    if (lows[id] !== indexes[id]) return;
    const component = [];
    let member = null;
    while (member !== id) {
      member = stack.pop();
      onStack.delete(member);
      component.push(member);
    }
    components.push(component);
  };
  for (const id of ids) if (isNil(indexes[id])) visit(id);
  return components;
};

const automaticLoops = (nodes, edges) => {
  const automatic = new Set(
    nodes.filter((node) => isAutomatic(node, nodes)).map((node) => node.id),
  );
  const loopEdges = edges.filter(
    (edge) =>
      isRoutingEdge(edge) &&
      automatic.has(edge.source) &&
      automatic.has(edge.target),
  );
  const edgesBySource = groupBy(loopEdges, "source");
  return stronglyConnected([...automatic], edgesBySource)
    .map((component) => {
      const members = new Set(component);
      const inner = loopEdges.filter(
        (edge) => members.has(edge.source) && members.has(edge.target),
      );
      return { members, inner };
    })
    .filter(({ inner }) => inner.length > 0);
};

const recommendationChecks = (nodes, needsPaths) => {
  const savesNeeds = nodes.some((node) => needsPathsOfStep(node).length > 0);
  const known = isNil(needsPaths) ? null : new Set(needsPaths);
  return nodes.reduce((result, node) => {
    result[node.id] = [
      ...(!savesNeeds && showsRecommendedBadge(node)
        ? [check(CHECK_CODES.RECOMMENDATION_WITHOUT_NEEDS)]
        : []),
      ...(isNil(known)
        ? []
        : needsPathsOfStep(node)
            .filter((path) => !known.has(path))
            .map((path) =>
              check(CHECK_CODES.NEEDS_CATEGORY_MISSING, {
                category: needsCategoryOf(path),
              }),
            )),
    ];
    return result;
  }, {});
};

const checkedFieldChecks = (node, checkedFields) =>
  checkedFieldIssues(node, checkedFields).map((issue) =>
    issue.kind === "NO_CHECK"
      ? check(CHECK_CODES.CHECKED_FIELD_NO_CHECK, {
          field: issue.field.label,
        })
      : check(CHECK_CODES.CHECKED_FIELD_NOT_LINKED, {
          answer: get(issue.slot, "label") || "",
          fields: issue.fields.map((field) => field.label).join(" · "),
        }),
  );

const filledAccessors = (nodes, variables) => {
  const accessorByPath = new Map(
    (variables || []).map((variable) => [variable.field, variable.accessor]),
  );
  return new Set(
    nodes
      .flatMap(slotsOfStep)
      .map(slotPathOf)
      .filter(Boolean)
      .flatMap((path) => [path, accessorByPath.get(path)])
      .filter(Boolean),
  );
};

const requiredFieldChecks = (node, filled) => {
  const formDefinition = get(node, "data.formDefinition");
  if (!get(formDefinition, "strict")) return [];
  return (get(formDefinition, "fields") || [])
    .filter(
      (field) =>
        field.required && !field.display && !filled.has(field.accessor),
    )
    .map((field) =>
      check(CHECK_CODES.REQUIRED_NOT_ON_SCREEN, {
        field: field.label || field.accessor,
      }),
    );
};

export const checksByStep = (
  nodes = [],
  edges = [],
  startId = null,
  { needsPaths = null, checkedFields = [], variables = null } = {},
) => {
  const recommendation = recommendationChecks(nodes, needsPaths);
  const filled = isNil(variables) ? null : filledAccessors(nodes, variables);
  const reached = isNil(startId) ? null : reachableFrom(startId, edges);
  const nameOf = (id) =>
    get(
      nodes.find((node) => node.id === id),
      "data.name",
    ) || "—";
  const loopChecks = automaticLoops(nodes, edges).flatMap(
    ({ members, inner }) => {
      const loop = check(
        CHECK_CODES.AUTO_LOOP,
        { steps: [...members].map(nameOf).join(", ") },
        inner.every(isUnconditional)
          ? CHECK_LEVELS.ERROR
          : CHECK_LEVELS.WARNING,
      );
      return [...members].map((id) => ({ id, loop }));
    },
  );
  return nodes.reduce((result, node) => {
    result[node.id] = [
      ...stepChecks(node, nodes, edges),
      ...(reached && !reached.has(node.id)
        ? [check(CHECK_CODES.UNREACHABLE)]
        : []),
      ...loopChecks
        .filter((item) => item.id === node.id)
        .map((item) => item.loop),
      ...recommendation[node.id],
      ...checkedFieldChecks(node, checkedFields),
      ...(isNil(filled) ? [] : requiredFieldChecks(node, filled)),
    ];
    return result;
  }, {});
};

export const endSteps = (nodes = [], edges = []) =>
  nodes.filter((node) => !edges.some((edge) => edge.source === node.id));

export const worstLevel = (checks = []) => {
  if (checks.some((item) => item.level === CHECK_LEVELS.ERROR))
    return CHECK_LEVELS.ERROR;
  if (checks.length) return CHECK_LEVELS.WARNING;
  return null;
};
